import { useState } from "react";
import { func, object, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { getSnapshot } from "mobx-state-tree";
import { omit } from "lodash";
import { simplifyTextString } from "@utils";
import { useSearchableList } from "@hooks";
import { useEstimateEditorStore, useEstimateStructure } from "@tools";
import { Alert, TextClearInput, Radio } from "@components";
import { Grid } from "@material-ui/core";
import classnames from "classnames";

export const DescriptionTitleEditor = observer(({
  columnSettings,
  className,
  inputClassName,
  onNameAccepted,
  section,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { setCreatedDescription } = useEstimateEditorStore();

  const { id, displayName, descriptionIndex, autoFocus } = columnSettings;

  const [ nameToAccept, setNameToAccept ] = useState(false);
  const [ selectedScope, setScope ] = useState("all");

  const getOptionDisabled = (value) => Boolean(
    (section || structure).usedBreakdownColumns.find(c => (
      simplifyTextString(c.displayName) === simplifyTextString(value)
      && c.id !== id
      && c.visible
    ))
  );
  
  const handleNameChange = ({ name }) => {
    setCreatedDescription(false);

    const existingColumn = section?.usedBreakdownColumns.find(c => (
      simplifyTextString(c.displayName) === simplifyTextString(name) && c.id !== id
    ));

    if(!section || !autoFocus) {
      if(existingColumn) {
        existingColumn.setVisibility(true);
        return true;
      }
      structure.setBreakdownColumnDisplayName(id, name);
      onNameAccepted?.(name);
      return;
    }

    if(existingColumn) {
      setCreatedDescription(false);
      existingColumn.setVisibility(true);
      section.removeDescriptionColumn(id);
      structure.historyManager.stopGroup();
      return;
    }

    setNameToAccept(name);
  }

  const handleNameAccept = () => {
    try {
      columnSettings.setAutofocus(false);
      const position = section.usedBreakdownColumns.findIndex(c => c.id === id);
      section.usedBreakdownColumns[position].setDisplayName(nameToAccept);
      structure.acceptNewDescriptionColumn(
        section.id,
        { ...omit(getSnapshot(columnSettings), "autoFocus"), displayName: nameToAccept },
        position,
        selectedScope === "all"
      );
      setNameToAccept(null);
    } finally {
      structure.historyManager.stopGroup();
    }
  }

  const handleCancel = () => {
    setNameToAccept(null);
    setCreatedDescription(false);
    section?.removeDescriptionColumn(id);
    structure.historyManager.stopGroup();
  }

  const handleSelectScope = (e) => setScope(e.target.value);

  const { inputProps } = useSearchableList({
    errorOnEmpty: true,
    defaultValue: autoFocus ? "" : displayName || t("common.desc"),
    alternativeNewValue: displayName || t("common.desc"),
    autoFocus,
    onChange: handleNameChange,
    onBlur: handleCancel,
    getOptionDisabled,
  });
  
  return (
    <>
      <TextClearInput
        {...inputProps}
        noButtons
        multiline
        lockSpecialCharacters={false}
        placeholder={autoFocus ? displayName : t("common.desc")}
        scaleWidthToContent
        className={classnames(`descriptionEditor${descriptionIndex}`, className)}
        preventClickAwayOnElement={`descriptionEditor${descriptionIndex}`}
        inputClassName={inputClassName}
        preventBlurOnRelatedTarget
      />
      {
        Boolean(section && autoFocus) &&
        <Alert
          title={t("views.editor.breakdown_table.alert_titles.add")}
          isOpen={Boolean(nameToAccept)}
          acceptText={t("views.editor.breakdown_table.alert_titles.confirm")}
          onCancel={handleCancel}
          onAccept={handleNameAccept}
        >
          <Grid item container direction="column" className="gap-2 mt-2 mb-4">
            <Radio
              name="description-column-scope"
              aria-label="description-column-scope"
              value="all"
              checked={selectedScope === "all"}
              onChange={handleSelectScope}
              label={t("views.editor.breakdown_table.alert_titles.all_sections")}
            />
            <Radio
              name="description-column-scope"
              aria-label="description-column-scope"
              value="section"
              checked={selectedScope === "section"}
              onChange={handleSelectScope}
              label={t("views.editor.breakdown_table.alert_titles.this_section")}
            />
          </Grid>
        </Alert>
      }
    </>
  )
})

DescriptionTitleEditor.propTypes = {
  columnSettings: object.isRequired,
  className: string,
  inputClassName: string,
  onNameAccepted: func,
  section: object,
}
